import React, { useState, useRef, useEffect } from 'react';
import { X, Check, Clock, ArrowLeft, XCircle, History, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useHallPassTypes } from '../../contexts/HallPassTypesContext';
import { UserContext } from '../../contexts/UserContext';
import axios from '../../axios-config';
import { startNotificationConnection } from '../../services/notificationHubService';
import { toast } from 'react-toastify';
import { pushDesktopNotification } from '../Notifications/Notifications'; // Assuming this is the correct import
import { HallPassStatuses } from '../../types/enums';   // ← new
import { LoadingContext } from '../../contexts/LoadingContext';
import { hallPassApiService, MapPathUserAssignment } from '../../services/hallPassApiService';

interface HallPassModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type RequestStatus = 'editing' | 'pending' | 'approved' | 'denied' | 'summons';

export function HallPassModal({ isOpen, onClose }: HallPassModalProps) {
  const navigate = useNavigate();
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [isValidTeacher, setIsValidTeacher] = useState(true);
  const [requestStatus, setRequestStatus] = useState<RequestStatus>('editing');
  const [selectedDestination, setSelectedDestination] = useState('');
  const [showTeacherDropdown, setShowTeacherDropdown] = useState(false);
  const [reason, setReason] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(600); // 10 minutes in seconds
  const [hallPassId, setHallPassId] = useState<number | null>(null);
  const [summonsMessage, setSummonsMessage] = useState(''); // 👉 new
  const [isSubmitting, setIsSubmitting] = useState(false);  // ★ NEW – prevent dup/invalid submits
  // Teacher path preferences (MapPathUser assignments) retained for potential UI usage
  const [teacherPreferences, setTeacherPreferences] = useState<MapPathUserAssignment[]>([]);
  const [preferredHallPassTypes, setPreferredHallPassTypes] = useState<any[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const { startLoading, stopLoading } = React.useContext(LoadingContext);

  const { hallPassTypes } = useHallPassTypes();
  const { userProfile } = React.useContext(UserContext);
  const profileTeachers = userProfile?.teachers || [];

  useEffect(() => {
    if (!selectedDestination && hallPassTypes.length > 0) {
      const first = hallPassTypes[0];
      setSelectedDestination(first.hallPassTypeName || first.hallPassTypeDescription || '');
    }
  }, [hallPassTypes, selectedDestination]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        inputRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowTeacherDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (requestStatus === 'pending' && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            if (timerRef.current) clearInterval(timerRef.current);
            handleCancelRequest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [requestStatus, timeRemaining]);

  useEffect(() => {
    if (!isOpen) {
      setTimeRemaining(600);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (requestStatus !== 'pending') {
        resetForm();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    startNotificationConnection(
      (hp: any) => {
        console.log('Notification received:', hp);

        // Note: Student ID filtering is now handled at the notification service level,
        // but we keep this validation as a backup defense layer
        const actualHallPass = hp.hp || hp; // Handle nested structure from ReturnHallPass/ExpiredHallPass
        const hallPassStudentId = actualHallPass.studentId;
        
        if (hallPassStudentId && userProfile?.userId) {
          if (hallPassStudentId !== userProfile.userId) {
            console.log('Hall pass notification not for current student, ignoring');
            return;
          }
        } else if (!userProfile?.userId) {
          console.log('No user profile available, ignoring hall pass notification');
          return;
        }
        // If hallPassStudentId is undefined, we allow the notification through for backward compatibility

        switch (hp.status) {
          case 'expired':
            toast.error(`Hall pass request ${hp.desc} expired`, { autoClose: 25000 });
            resetForm();
            onClose();
            break;
          case 'returned':
            resetForm();
            onClose();
            break;
          case 'approved':
            setHallPassId(hp.hallPassId ?? hp.id ?? null); // ←–– capture the incoming pass ID so "I'm back!" can work
            setRequestStatus('approved');
            toast.success(`Hall pass ${hp.desc} approved`, { autoClose: 25000 });
            break;

          case 'denied':
            setRequestStatus('denied');
            toast.error(`Hall pass ${hp.desc} denied`, { autoClose: 25000 });
            break;

          case 'summons':
            setRequestStatus('summons');
            setSelectedDestination(hp.desc ?? '');   // ensure destination shown
            setReason(hp.notes ?? '');               // keep notes for later render
            setSummonsMessage(hp.message ?? '');     // 👉 capture the main message
            toast.info(
              <>
                <div>{hp.message}</div>
                {hp.notes && (
                  <div>
                    <strong>Notes:</strong> {hp.notes}
                  </div>
                )}
              </>,
              { autoClose: false }
            );
            break;

          default:
            console.warn('Unknown payload:', hp);
        }
      },
      'STUDENT',  // ensure we pass STUDENT here
      undefined,  // onOrder parameter
      userProfile?.userId || undefined  // Pass userId for filtering at notification service level
    );
  }, [isOpen, requestStatus, userProfile?.userId]);

  useEffect(() => {
    if (requestStatus === 'editing') return;
    switch (requestStatus) {
      case 'pending':
        pushDesktopNotification('Hall Pass Request Pending', { body: `Your hall pass request for ${selectedDestination} is pending approval from ${selectedTeacher}.` });
        break;
      case 'approved':
        pushDesktopNotification('Hall Pass Request Approved', { body: `Your hall pass request for ${selectedDestination} has been approved.`  });
        break;
      case 'denied':
        pushDesktopNotification('Hall Pass Request Denied', { body: `Your hall pass request for ${selectedDestination} has been denied.` });
        break;
      case 'summons':
        pushDesktopNotification('Student Summons', { body: summonsMessage ? summonsMessage : `You have received a summons. Reason: ${reason}` });
        break;
      default:
        break;
    }
  }, [requestStatus]);

  const resetForm = () => {
    setSelectedTeacher('');
    setIsValidTeacher(true);
    setRequestStatus('editing');
    setSelectedDestination(
      hallPassTypes.length > 0
        ? (hallPassTypes[0].hallPassTypeName || hallPassTypes[0].hallPassTypeDescription || '')
        : ''
    );
    setReason('');
    setTimeRemaining(600);
    setHallPassId(null);
    setTeacherPreferences([]);
    setPreferredHallPassTypes([]);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const filteredTeachers = profileTeachers
    .filter(teacher =>
      teacher.name.toLowerCase().includes(selectedTeacher.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  // Fetch teacher's hall pass type preferences when a teacher is selected
  useEffect(() => {
    const fetchTeacherPreferences = async () => {
      if (!selectedTeacher) return;
      
      const teacherObj = profileTeachers.find(t => t.name === selectedTeacher);
      if (!teacherObj) return;

      try {
        const prefs = await hallPassApiService.getUserPathAssignments(teacherObj.id);
        const sortedPrefs = [...prefs].sort((a, b) => a.sortOrder - b.sortOrder);
        setTeacherPreferences(sortedPrefs); // derive in separate effect
      } catch (err) {
        console.error('Failed to fetch teacher preferences:', err);
      }
    };

    fetchTeacherPreferences();
  }, [selectedTeacher, profileTeachers]);

  // Derive preferredHallPassTypes from teacherPreferences
  useEffect(() => {
    if (teacherPreferences.length > 0) {
      const preferredTypes = teacherPreferences
        .map(pref => hallPassTypes.find(type => type.path?.pathId === pref.pathId))
        .filter(Boolean) as any[];
      setPreferredHallPassTypes(preferredTypes);
      if (preferredTypes.length > 0 && requestStatus === 'editing') {
        const defaultType = preferredTypes[0];
        if (defaultType) {
          setSelectedDestination(defaultType.hallPassTypeName || defaultType.hallPassTypeDescription || '');
        }
      }
    } else {
      setPreferredHallPassTypes(hallPassTypes);
    }
  }, [teacherPreferences, hallPassTypes, requestStatus]);

  // hydrate from userProfile.hallpasses on mount/updates
  useEffect(() => {
    if (!userProfile?.hallpasses?.length) return;
    userProfile.hallpasses
      .filter(hp =>
        (Boolean(hp.summonedBy)         // any active summons
        || hp.hallPassStatusId === HallPassStatuses.Active
        || hp.hallPassStatusId === HallPassStatuses.Pending)
        && !hp.returnAt               // not returned
        && hp.hallPassStatusId !== HallPassStatuses.ExpiredRequest
        && hp.hallPassStatusId !== HallPassStatuses.ExpiredReturn // not expired
        && hp.hallPassStatusId !== HallPassStatuses.Returned // not returned
      )
      .forEach(hp => {
        const type = hallPassTypes.find(t => t.hallPassTypeId === hp.hallPassTypeId);
        const desc = type
          ? (type.hallPassTypeName || type.hallPassTypeDescription || '')
          : '';
        const teacherName =
          profileTeachers.find(t => t.id === hp.teacherId)?.name || '';

        // prioritize summonedBy as summons
        if (hp.summonedBy) {
          setRequestStatus('summons');
          setSelectedDestination(hp.summonedTo ?? '');
          setReason(hp.notes ?? '');
          setSummonsMessage(`Summoned by ${hp.summonedBy}`);
        } else {
          switch (hp.hallPassStatusId) {
            case HallPassStatuses.Pending:
              setSelectedTeacher(teacherName);
              setSelectedDestination(desc);
              setReason(hp.notes);
              setHallPassId(hp.hallPassId);
              // initialize timer based on creation time
              const createdMs = new Date(hp.createdAt).getTime();
              const elapsedSec = Math.floor((Date.now() - createdMs) / 1000);
              setTimeRemaining(Math.max(0, 600 - elapsedSec));
              setRequestStatus('pending');
              break;
            case HallPassStatuses.Active:
              setSelectedTeacher(teacherName);
              setSelectedDestination(desc);
              setReason(hp.notes);
              setHallPassId(hp.hallPassId);
              setRequestStatus('approved');
              break;
            case HallPassStatuses.ExpiredReturn:
            case HallPassStatuses.Returned:
              // ...optionally handle returned...
              break;
            // add summons mapping if you have a statusId for summons
          }
        }
      });
  }, [userProfile, hallPassTypes]);  // removed isOpen

  // only hide when closed _and_ still in editing state
  if (!isOpen && requestStatus === 'editing') return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;       // guard

    // --- new: enforce hallpassesCooldown ---
    const cooldown = parseInt((userProfile as any)?.hallpassesCooldown ?? '0', 10);
    if (cooldown > 0) {
      const recentReturn = ((userProfile as any).hallpasses ?? [])
        .filter((hp: { returnAt: any; }) => hp.returnAt)
        .sort(
          (a: { returnAt: string | number | Date; }, b: { returnAt: string | number | Date; }) =>
            new Date(b.returnAt!).getTime() - new Date(a.returnAt!).getTime()
        )[0];
      if (recentReturn) {
        const minutesSince =
          (Date.now() - new Date(recentReturn.returnAt!).getTime()) / 60000;
        if (minutesSince < cooldown) {
          const remaining = Math.ceil(cooldown - minutesSince);
          toast.info(
            `Please wait ${remaining} more minute(s) before requesting a new hall pass or ask your teacher directly.`
          );
          return;
        }
      }
    }
    // --- end cooldown check ---

    const teacherObj = profileTeachers.find(t => t.name === selectedTeacher);
    if (!teacherObj) {
      setIsValidTeacher(false);
      return;
    }
    // Derive hall pass type matching the selectedDestination string
    let selectedType = hallPassTypes.find(
      type => (type.hallPassTypeName || type.hallPassTypeDescription || '') === selectedDestination
    );
    if (!selectedType && hallPassTypes.length > 0) {
      selectedType = hallPassTypes[0];
    }

    setIsSubmitting(true);   // lock submit
    startLoading();

    try {
      const payload = {
        organizationId: teacherObj?.orgId,
        studentId: userProfile?.userId,
        teacherId: teacherObj?.id,
        hallPassTypeId: selectedType?.hallPassTypeId,
        notes: reason
      };
      const result = await axios.post('/api/HallPasses', payload);
      const createdHp = result.data;
      setHallPassId(createdHp.hallPassId);
      setRequestStatus('pending');
      setTimeRemaining(600);
      // add returned hall pass into local userProfile for cooldown checks
      if (userProfile?.hallpasses) {
        userProfile.hallpasses = [...userProfile.hallpasses, createdHp];
      }
    } catch (err: any) {
      // NEW: show server-sent message on 400 responses
      if (err?.response?.status === 400) {
        const msg = err.response.data?.message ?? err.response.data ?? 'Bad request';
        toast.info(msg);              // same toast style used earlier (line 265)
      } else {
        console.error(err);
        toast.error('Unable to submit hall pass request.');
      }
    } finally {
      setIsSubmitting(false);       // unlock even on error
      stopLoading();
    }
  };

  const handleBack = () => {
    setRequestStatus('editing');
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleReturnRequest = async () => {
    startLoading();
    try {
      if (requestStatus === 'approved' && hallPassId) {
  // Server performs authorization; avoid brittle local checks that may be stale
        const now = new Date().toISOString();
        await axios.put(
          `/api/HallPasses/ReturnHallPass/${hallPassId}`,
          { returnAt: now }
        );
        // reflect returnAt locally for cooldown checks
        if (userProfile?.hallpasses) {
          const [match] = userProfile.hallpasses.filter(hp => hp.hallPassId === hallPassId);
          if (match) match.returnAt = now;
        }
        resetForm();
        onClose();
      }
    } catch (err: any) {
      console.error(err);
      // Surface message from server if permission denied
      const msg = err?.response?.data?.message || 'Failed to return hall pass.';
      toast.error(msg);
    } finally {
      stopLoading();
    }
  };

  const handleCancelRequest = async () => {
    startLoading();
    try {
      if (requestStatus === 'pending' && hallPassId) {
        await axios.put(`/api/HallPasses/CancelHallPass/${hallPassId}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
    resetForm();
    onClose();
  };

  const formatTimeRemaining = () => {
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const timeRemainingPercentage = (timeRemaining / 600) * 100;

  const getTimerColor = () => {
    if (timeRemaining > 300) return 'bg-green-500';
    if (timeRemaining > 120) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-40">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative">
          <button
            onClick={handleCancelRequest}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>

        <div className="p-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">
            {requestStatus === 'editing'
              ? 'Student Hall Pass Request'
              : requestStatus === 'pending'
              ? `Request Pending for Hall Pass ID: ${selectedDestination}`
              : requestStatus === 'approved'
              ? `Request Approved for Hall Pass ID: ${selectedDestination}`
              : requestStatus === 'summons'
              ? `Student Summons`
              : `Request Denied for Hall Pass ID: ${selectedDestination}`}
          </h2>

          {requestStatus === 'editing' ? (
            <>
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
                <p className="text-gray-600">Logged in as: Alex Johnson</p>
                <p className="text-gray-600">Student ID: S12345</p>
                <p className="text-gray-600">Grade: 10</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Request From Teacher/Staff
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      ref={inputRef}
                      value={selectedTeacher}
                      onChange={(e) => {
                        setSelectedTeacher(e.target.value);
                        setShowTeacherDropdown(true);
                        setIsValidTeacher(true);
                      }}
                      onFocus={() => setShowTeacherDropdown(true)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                        isValidTeacher ? 'border-gray-300' : 'border-red-500'
                      }`}
                      placeholder="Start typing teacher's name..."
                      required
                    />
                    {!isValidTeacher && (
                      <p className="mt-1 text-sm text-red-600">
                        Please select a teacher from the list
                      </p>
                    )}
                    
                    {showTeacherDropdown && filteredTeachers.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                        <div ref={dropdownRef}>
                        {filteredTeachers.map((teacher) => (
                          <button
                            key={teacher.id}
                            type="button"
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between group"
                            onClick={() => {
                              setSelectedTeacher(teacher.name);
                              setShowTeacherDropdown(false);
                            }}
                          >
                            <span>{teacher.name}</span>
                            <Check 
                              size={16} 
                              className={`text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity ${
                                selectedTeacher === teacher.name ? 'opacity-100' : ''
                              }`}
                            />
                          </button>
                        ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type of Pass
                  </label>
                  <select
                    value={selectedDestination}
                    onChange={(e) => setSelectedDestination(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    required
                  >
                    {(preferredHallPassTypes.length > 0 ? preferredHallPassTypes : hallPassTypes).map((type) => (
                      <option
                        key={type.hallPassTypeId}
                        value={type.hallPassTypeName || type.hallPassTypeDescription || ''}
                      >
                        {type.hallPassTypeName || type.hallPassTypeDescription || 'Unknown'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason (Optional)
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                    rows={3}
                    placeholder="Brief explanation if needed"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}                     // ★ prevent duplicate submits
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Request Pass
                </button>
                <div className="flex justify-center mt-4">
                <a
                  onClick={() => {
                    handleCancelRequest();
                    navigate('/history');
                  }}
                  className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
                  style={{ cursor: 'pointer' }}
                >
                  <History size={16} />
                  View Pass History
                </a>
                </div>
              </form>
            </>
          ) : requestStatus === 'pending' ? (
            <div className="space-y-6">
              <div className="flex items-center justify-center">
                <div className="bg-indigo-100 rounded-full p-3">
                  <Clock size={32} className="text-indigo-600 animate-pulse" />
                </div>
              </div>
              
              <div className="text-center space-y-2">
                <p className="text-lg font-medium text-gray-900">
                  Waiting for {selectedTeacher}'s Approval
                </p>
                <p className="text-sm text-gray-500">
                  Your request to visit the {selectedDestination.split(' ')[0]} is being reviewed
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Request expires in:</span>
                  <span className={`text-sm font-bold ${timeRemaining < 120 ? 'text-red-600' : timeRemaining < 300 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {formatTimeRemaining()}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className={`${getTimerColor()} h-2.5 rounded-full transition-all duration-1000 ease-linear`} 
                    style={{ width: `${timeRemainingPercentage}%` }}
                  ></div>
                </div>
                {timeRemaining < 120 && (
                  <div className="flex items-center gap-2 text-red-600 text-xs">
                    <AlertTriangle size={14} />
                    <span>Request will auto-cancel when timer expires</span>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Destination:</span>
                  <span className="text-gray-900 font-medium">{selectedDestination}</span>
                </div>
                {reason && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Reason:</span>
                    <span className="text-gray-900 font-medium">{reason}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleBack}
                  className="flex items-center justify-center gap-2 flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Edit Request
                </button>
                <button
                  onClick={handleCancelRequest}
                  className="flex items-center justify-center gap-2 flex-1 py-2 px-4 bg-red-50 border border-red-200 rounded-md text-red-600 hover:bg-red-100 transition-colors"
                >
                  <XCircle size={16} />
                  Cancel Request
                </button>
              </div>
            </div>
          ) : requestStatus === 'approved' ? (
            <div className="space-y-6">
              <div className="flex items-center justify-center">
                <div className="bg-green-100 rounded-full p-3">
                  <Check size={32} className="text-green-600" />
                </div>
              </div>
              
              <div className="text-center space-y-2">
                <p className="text-lg font-medium text-gray-900">
                  Request Approved
                </p>
                <p className="text-sm text-gray-500">
                  You may proceed to the {selectedDestination.split(' ')[0]}.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Destination:</span>
                  <span className="text-gray-900 font-medium">{selectedDestination}</span>
                </div>
                {reason && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Reason:</span>
                    <span className="text-gray-900 font-medium">{reason}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleReturnRequest}
                  className="flex items-center justify-center gap-2 flex-1 py-2 px-4 bg-indigo-50 border border-indigo-200 rounded-md text-indigo-600 hover:bg-indigo-100 transition-colors"
                >
                  I'm back!
                </button>
              </div>
            </div>
          )  : requestStatus === 'summons' ? (
            <div className="space-y-6">
              <div className="flex items-center justify-center">
                <div className="bg-yellow-100 rounded-full p-3">
                  <AlertTriangle size={32} className="text-yellow-600" />
                </div>
              </div>
              
              <div className="text-center space-y-2">
                <p className="text-lg font-medium text-gray-900">{summonsMessage}</p>
                {reason && (
                  <p className="text-sm text-gray-500">
                    <strong>Notes:</strong> {reason}
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCancelRequest}
                  className="flex items-center justify-center gap-2 flex-1 py-2 px-4 bg-indigo-50 border border-indigo-200 rounded-md text-indigo-600 hover:bg-indigo-100 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-center">
                <div className="bg-red-100 rounded-full p-3">
                  <XCircle size={32} className="text-red-600" />
                </div>
              </div>
              
              <div className="text-center space-y-2">
                <p className="text-lg font-medium text-gray-900">
                  Request Denied
                </p>
                <p className="text-sm text-gray-500">
                  Your request to visit the {selectedDestination.split(' ')[0]} was not approved.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Destination:</span>
                  <span className="text-gray-900 font-medium">{selectedDestination}</span>
                </div>
                {reason && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Reason:</span>
                    <span className="text-gray-900 font-medium">{reason}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleBack}
                  className="flex items-center justify-center gap-2 flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Edit Request
                </button>
                <button
                  onClick={handleCancelRequest}
                  className="flex items-center justify-center gap-2 flex-1 py-2 px-4 bg-red-50 border border-red-200 rounded-md text-red-600 hover:bg-red-100 transition-colors"
                >
                  Cancel Request
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </>
  );
}